import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'

type ModalProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg' | 'xl'
}

const WIDTHS = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-xl', xl: 'max-w-2xl' }

export function Modal({ open, onClose, title, children, footer, width = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])
  if (!open) return null
  return (
    // The overlay scrolls, so a dialog taller than a short window is reached
    // by scrolling rather than clipped; the dialog body scrolls on its own too.
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60"
      onMouseDown={(e) => {
        // popovers (token picker) are portaled outside the dialog element
        if (!(e.target as HTMLElement).closest('[role="dialog"], [data-popover]')) onClose()
      }}
    >
      <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          className={`card flex max-h-[calc(100vh-2rem)] w-full ${WIDTHS[width]} flex-col overflow-hidden shadow-2xl`}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button type="button" className="btn btn-ghost !p-1" onClick={onClose} aria-label="Close">
              <X className="size-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">{children}</div>
          {footer ? <div className="border-t border-line px-6 py-4">{footer}</div> : null}
        </div>
      </div>
    </div>
  )
}
